import {useLazyQuery, useMutation} from "@apollo/client";
import {upperFirst} from "lodash";
import isEmpty from "lodash/isEmpty";
import pluralize from "pluralize";
import React, {ForwardRefExoticComponent, RefAttributes, useState} from "react";
import {toast} from "react-hot-toast";
import {useNavigate} from "react-router";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {shortDateTimeUser} from "@/lib/format.tsx";
import {GQL_USER_CONTEXT} from "@/lib/queries/accounts.ts";
import {
  AdditionalLimitInput,
  CheckSubscriptionLimitResponse,
  GQL_ACCOUNT_STATS,
  GQL_CHECK_SUBSCRIPTION_LIMIT,
  GQL_CHECKOUT_CONTEXT,
  GQL_INSTANT_CHECKOUT_V2,
  GQL_PURCHASE_REQUEST_V2,
  InstantCheckoutV2Response,
  PurchaseRequestV2Response,
} from "@/lib/queries/billing.ts";
import {AccountSubscriptionType, AccountType} from "@/lib/types/accounts.ts";
import {BillingPeriod} from "@/lib/types/billing.ts";
import {MonitoringServiceType} from "@/lib/types/checks.ts";
import {PURCHASE_ITEM_NAMES} from "@/pages/billing/utils.ts";
import {
  InstantCheckoutPopup,
  InstantCheckoutReasonAlert,
} from "@/pages/billing/v2/InstantCheckout/components/InstantCheckoutPopup.tsx";

export interface LimitCheckProps {
  limitField?: string;
  requestedValue?: number;
  requestedIncrement?: number;
  serviceType?: MonitoringServiceType;
  additionalLimits?: AdditionalLimitInput[];
}

export interface LimitCheckWithInstantCheckoutProps extends LimitCheckProps {
  getTitleMessage: (hasSubscription: boolean, limitName: string, limitValue: number) => string;
  getRequestMessage: (hasSubscription: boolean, limitName: string, limitValue: number) => string;
  getUpgradeMessage?: (hasSubscription: boolean, limitName: string, limitValue: number) => string;
  getReasonMessage?: (hasSubscription: boolean, limitName: string, limitValue: number) => string;
  suppressUnavailable?: boolean;
  showTierLimits?: boolean;
  showCustomizeSubscription?: boolean;
  onUpgradeSuccess?: () => void;
  triggerContext?: string;
}

export const LimitCheckWithInstantCheckout = React.forwardRef(
  (
    {
      limitField,
      requestedValue,
      requestedIncrement,
      serviceType,
      additionalLimits,
      getTitleMessage,
      getRequestMessage,
      getReasonMessage,
      getUpgradeMessage,
      suppressUnavailable,
      showTierLimits = true,
      showCustomizeSubscription,
      onUpgradeSuccess,
      triggerContext,
    }: LimitCheckWithInstantCheckoutProps,
    ref
  ) => {
    const {user} = useCurrentUserContext();
    const navigateFn = useNavigate();
    const [checkLimits, {loading, data}] = useLazyQuery<CheckSubscriptionLimitResponse>(GQL_CHECK_SUBSCRIPTION_LIMIT, {
      fetchPolicy: "no-cache",
      nextFetchPolicy: "no-cache",
    });

    const [instantCheckout, {data: instantCheckoutData, loading: instantCheckoutLoading}] =
      useMutation<InstantCheckoutV2Response>(GQL_INSTANT_CHECKOUT_V2, {
        refetchQueries: [GQL_CHECKOUT_CONTEXT, GQL_ACCOUNT_STATS],
      });
    const [purchaseRequest, {loading: purchaseRequestLoading}] =
      useMutation<PurchaseRequestV2Response>(GQL_PURCHASE_REQUEST_V2);

    const [showInstantCheckout, setShowInstantCheckout] = useState(false);
    const requestSent = !isEmpty(data?.checkSubscriptionLimit.pending_purchase_request_at);
    const mustRequest =
      !user?.is_primary && (user?.account.type !== AccountType.REGULAR || !user?.has_purchase_permission);
    const currentLimitValue = data?.checkSubscriptionLimit.current_limit || 0;
    const limitName = data?.checkSubscriptionLimit.limit_name || "";
    const hasSubscription = isEmpty(user?.account.free_trial_expires_at);
    const btnTitle = mustRequest
      ? `Request ${hasSubscription ? "Upgrade" : "Subscription"}`
      : user?.account.type === AccountType.REGULAR
        ? hasSubscription
          ? "Upgrade Subscription"
          : "Subscribe Now"
        : "Contact Support";

    const toReasonAlert = (name: string, value: number): InstantCheckoutReasonAlert => {
      if (mustRequest && requestSent) {
        return {
          callout: "Upgrade request sent successfully",
          body: `An upgrade request was sent to your account owner on ${shortDateTimeUser(data!.checkSubscriptionLimit.pending_purchase_request_at, user!)}. Once it is completed, the requested items will be added to your account.`,
        };
      }
      let reasonMessage = "";
      if (getReasonMessage) {
        reasonMessage =
          value > 0
            ? getReasonMessage(hasSubscription, name, value)
            : `${pluralize(name)} are not available on your ${hasSubscription ? "current plan" : "Free Trial"}`;
      }
      const body = mustRequest
        ? getRequestMessage(hasSubscription, name, value)
        : getUpgradeMessage?.(hasSubscription, name, value);
      return {callout: upperFirst(reasonMessage), body: body ?? ""};
    };

    const exhaustedAdditionalLimits = mustRequest
      ? []
      : (data?.checkSubscriptionLimit.exhausted_additional_limits ?? []);
    const reasonAlerts = [
      toReasonAlert(limitName, currentLimitValue),
      ...exhaustedAdditionalLimits.map(({limit_field, current_limit}) =>
        toReasonAlert(PURCHASE_ITEM_NAMES[limit_field], current_limit)
      ),
    ].filter(({callout}) => callout);

    const onCustomize = async () => {
      // Non-regular accounts can only be sent to custom quote page
      if (user?.account.type !== AccountType.REGULAR) {
        navigateFn("/billing/custom-quote", {
          state: {
            custom_monitoring_needs: `${data?.checkSubscriptionLimit.next_tier!.limits.map((limit) => `${limit.value_alt || limit.value} ${limit.limit_name}`).join(", ")}`,
          },
        });
      } else {
        navigateFn("/billing?action=subscribe", {
          state: {
            selectedTier: data?.checkSubscriptionLimit.next_tier,
          },
        });
      }
    };

    const checkIfLimitHit = async (props?: LimitCheckProps) => {
      if (user?.account.subscription_type !== AccountSubscriptionType.BILLING_2_0) {
        return false;
      }
      try {
        const result = await checkLimits({
          variables: {
            limitField: limitField || props?.limitField,
            serviceType: serviceType || props?.serviceType,
            requestedValue: requestedValue || props?.requestedValue,
            requestedIncrement: requestedIncrement || props?.requestedIncrement || 1,
            additionalLimits: additionalLimits || props?.additionalLimits,
          },
        });
        if (result.data?.checkSubscriptionLimit?.is_exhausted === true) {
          if (result.data.checkSubscriptionLimit.current_limit === 0 && suppressUnavailable) {
            return false; // Feature is unavailable, but we don't want to force it
          }
          setShowInstantCheckout(true);
          return true;
        } else {
          return false;
        }
      } catch (e) {
        console.error("Error with instant checkout.", e);
        return false;
      }
    };

    React.useImperativeHandle(ref, () => ({
      checkIfLimitHit,
    }));
    return (
      <InstantCheckoutPopup
        showTierLimits={showTierLimits}
        currentTier={data?.checkSubscriptionLimit.current_tier}
        nextTier={data?.checkSubscriptionLimit.next_tier}
        billingPeriod={data?.checkSubscriptionLimit.current_tier ? user?.account.billing_period : BillingPeriod.YEAR}
        hasSubscription={hasSubscription}
        lines={data?.checkSubscriptionLimit.pack_lines || []}
        reasonAlerts={reasonAlerts}
        titleMessage={getTitleMessage(hasSubscription, limitName, currentLimitValue)}
        includeContactMessage={mustRequest}
        btnTitle={instantCheckoutLoading || purchaseRequestLoading ? "Working..." : btnTitle}
        show={showInstantCheckout}
        loading={instantCheckoutLoading || purchaseRequestLoading || loading}
        errors={instantCheckoutData?.instantCheckoutV2.errors.__all__ || []}
        disableConfirmButton={mustRequest && requestSent}
        onCancel={() => setShowInstantCheckout(false)}
        onCustomize={showCustomizeSubscription ? onCustomize : undefined}
        onConfirm={async () => {
          if (!mustRequest) {
            // Non-regular accounts can only be sent to custom quote page
            if (user?.account.type !== AccountType.REGULAR) {
              navigateFn("/billing/custom-quote", {
                state: {
                  custom_monitoring_needs: `${data?.checkSubscriptionLimit.next_tier!.limits.map((limit) => `${limit.value_alt || limit.value} ${limit.limit_name}`).join(", ")}`,
                },
              });
            } else if (!hasSubscription) {
              // Regular account, but no subscription yet. We can't do instant checkout here,
              // because this is Free Trial, and there can be other packs involved,
              // and we can't add them automatically without knowing customer's real
              // intentions. So we take them to the subscription page where they can choose, unless
              // showCustomizeSubscription is set, then we take them to checkout directly.
              if (showCustomizeSubscription) {
                navigateFn("/billing/checkout", {
                  state: {
                    billingPeriod: BillingPeriod.YEAR,
                    bundle: [{packTitle: "Website Monitoring", ...data?.checkSubscriptionLimit.next_tier}],
                  },
                });
              } else {
                navigateFn("/billing?action=subscribe", {
                  state: {
                    selectedTier: data?.checkSubscriptionLimit.next_tier,
                  },
                });
              }
            } else {
              // This is a regular account with existing subscription, so we can proceed with
              // actual instant checkout - we already have subscription, so if there
              // should be other tiers involved (from different packs) - backend will
              // take care of them.
              const originalTrigger = triggerContext
                ? `Instant Checkout: ${limitName} (${triggerContext})`
                : `Instant Checkout: ${limitName}`;
              const result = await instantCheckout({
                variables: {
                  selectedTierId: data!.checkSubscriptionLimit.next_tier!.tier_id,
                  originalTrigger: originalTrigger,
                },
                refetchQueries: [GQL_USER_CONTEXT],
              });
              if (result.data?.instantCheckoutV2.success === true) {
                setShowInstantCheckout(false);
                toast.success("Upgrade successful — additional items have been allocated to your account.");
                onUpgradeSuccess?.();
              }
            }
          } else {
            // make a purchase request here and close the popup
            const result = await purchaseRequest({
              variables: {
                selectedTierId: data!.checkSubscriptionLimit.next_tier!.tier_id,
              },
            });
            if (result.data?.purchaseRequestV2.success === true) {
              setShowInstantCheckout(false);
              toast.success("Purchase request has been sent.");
            }
          }
        }}
      />
    );
  }
);

export interface LimitCheckWithInstantCheckoutComponent extends ForwardRefExoticComponent<
  LimitCheckWithInstantCheckoutProps & RefAttributes<unknown>
> {
  checkIfLimitHit: (props?: LimitCheckProps) => Promise<boolean>;
}

export interface LimitCheckerComponent extends ForwardRefExoticComponent<RefAttributes<unknown>> {
  checkIfLimitHit: (props?: LimitCheckProps) => Promise<boolean>;
}
